<script>
import CustomClientField from './CustomClientField';
import CustomFieldType from './CustomFieldType';
import { ErrorBag } from '@/system/helpers/Validate';
import { randomId } from '@/system/helpers/index.js';
import { cloneDeep } from 'lodash';

const FieldWidth = {
    Half: 0.5,
    Full: 1,
};

export default {
    props: {
        id: {
            required: false,
            default: () => randomId(),
        },
        displayStyle: {
            default: 'default',
        },
        context: {
            default: 'default',
        },

        customFields: {
            required: true,
            type: Array,
        },

        value: {
            required: false,
            default: () => {}
        },

        validationErrors: {
            required: false,
            default: () => new ErrorBag()
        },

        errorsPrefix: {
            required: false,
            default: null
        },

        usePlaceholders: {
            required: false,
            default: false,
        },
    },

    data() {
        return {
            FieldWidth,
            allowedDisplayStyles: ['default', 'inline'],
        }
    },

    mounted() {
        if (!this.allowedDisplayStyles.includes(this.displayStyle)) {
            console.error('CustomClientFieldsContainer can only have a "displayStyle" property with a value of "default" or "inline".');
        }
    },

    methods: {
        fieldUpdated(field, value) {
            this.$emit('input', {...this.value, [field.identifier]: value});
        },

        getErrorForField(field) {
            let fieldName = [this.errorsPrefix, field.identifier].filter(n => !!n).join('.');

            return this.validationErrors.first(fieldName);
        },
        shouldShowRequiredIndication(field){

            if(this.context === 'online_booking' && field.required_in_online_booking){
                return true;
            }

            if(this.context === 'events' && field.required_in_event_booking){
                return true;
            }

            return false;

        }
    },

    computed: {
        finalDisplayStyle() {
            if (!this.allowedDisplayStyles.includes(this.displayStyle)) {
                return 'default';
            }

            return this.displayStyle;
        },

        customFieldsOrdered() {
            let fields = [];

            // must find any textarea fields and make sure they're in even indices because they'll be taking full width of the form.
            for (let i = 0; i < this.customFields.length; i++) {
                if (fields.length % 2 === 0 || this.customFields[i].type !== CustomFieldType.Textarea) {
                    fields.push(this.customFields[i]);
                } else {
                    // swap the new textarea field with the previously added field
                    let previousField = fields.pop();

                    if (previousField.type === CustomFieldType.Textarea) {
                        fields.push(previousField, this.customFields[i]);
                    } else {
                        fields.push(this.customFields[i], previousField);
                    }
                }
            }

            return fields;
        },

        fieldsWithWidths() {
            let fields = cloneDeep(this.customFieldsOrdered)
            let fieldWidthSum = 0;

            for (let i = 0; i < fields.length; i++) {
                if (fieldWidthSum % 1 === 0 && fields.length === (i + 1)) {
                    // it's the last field and the previous row is full.
                    fields[i].width = FieldWidth.Full;
                } else if (fields[i].type === CustomFieldType.Textarea) {
                    // text area is always full width
                    fields[i].width = FieldWidth.Full;

                    if (fieldWidthSum % 1 === FieldWidth.Half) {
                        // if the previous field was half width, let's now make it full width.
                        fields[i - 1].width = FieldWidth.Full;
                        fieldWidthSum += FieldWidth.Half;
                    }
                } else if ([CustomFieldType.Radio, CustomFieldType.Checkbox].includes(fields[i].type)) {
                    // radio/checkboxes are dependent on the text length of their choices
                    const hasLongChoice = fields[i].field_choices.some(fc => fc.choice.length >= 45);

                    if (hasLongChoice) {
                        fields[i].width = FieldWidth.Full;

                        if (fieldWidthSum % 1 === FieldWidth.Half) {
                            // if the previous field was half width, let's now make it full width.
                            fields[i - 1].width = FieldWidth.Full;
                            fieldWidthSum += FieldWidth.Half;
                        }
                    } else {
                        fields[i].width = FieldWidth.Half;
                    }
                } else {
                    // in all other cases, just use half the width (col-sm-6)
                    fields[i].width = FieldWidth.Half;
                }

                fieldWidthSum += fields[i].width;
            }

            return fields;
        }
    },

    components: {
        CustomClientField,
    },
}
</script>

<template>
    <div class="row" v-if="finalDisplayStyle === 'default'">
        <div v-for="(field, index) in fieldsWithWidths" :key="`${id}_${field.identifier}_${index}`"
            :class="{ 'col-sm-6': field.width === FieldWidth.Half, 'col-sm-12': field.width === FieldWidth.Full }">

            <custom-client-field
                :field="field"
                :value="value[field.identifier]"
                @input="fieldUpdated(field, $event)"
                :error="getErrorForField(field)"
                :use-placeholder="usePlaceholders"
                :show-required-indicator="shouldShowRequiredIndication(field)"
            >
            </custom-client-field>

        </div>
    </div>
    <div v-else-if="finalDisplayStyle === 'inline'">
        <div v-for="(field, index) in customFieldsOrdered" :key="`${id}_${field.identifier}_${index}`">

            <custom-client-field
                :field="field"
                :value="value[field.identifier]"
                @input="fieldUpdated(field, $event)"
                :error="getErrorForField(field)"
                :use-placeholder="false"
                :displayStyle="finalDisplayStyle"
                :show-required-indicator="shouldShowRequiredIndication(field)"
            >
            </custom-client-field>
        </div>
    </div>
</template>
